/**
 * Wires "Watch the video" CTAs (.js-action[data-action="watch-video"]) to a
 * full-screen video overlay. Extracted from the legacy jQuery js-action
 * dispatcher in assets/custom.js so jQuery-free pages (the sanitized MediSpa
 * landing pages) can play the video without loading custom.js.
 *
 * The overlay elements are created on first use — the sanitized LP layout
 * does not render the static .video-overlay__wrapper / .generic-overlay-bg
 * markup that theme.medispa-redesign.liquid provides. Styles come from
 * medispa-hero-video.css (.video-overlay__wrapper, loaded by the hero video
 * section) and custom.css (.generic-overlay-bg, loaded by the LP layout).
 */

interface VideoSource {
  url?: string;
  mime_type?: string;
  width?: number;
  height?: number;
}

interface VideoConfig {
  aspect_ratio?: number;
  sources?: VideoSource[];
}

function getOrCreateOverlayBg(): HTMLElement {
  let bg = document.querySelector<HTMLElement>('.generic-overlay-bg');

  if (!bg) {
    bg = document.createElement('div');
    bg.className = 'generic-overlay-bg';
    document.body.appendChild(bg);
  }

  return bg;
}

function getOrCreatePlayer(): { wrapper: HTMLElement; video: HTMLVideoElement } {
  let wrapper = document.querySelector<HTMLElement>('.video-overlay__wrapper');
  let video = wrapper?.querySelector('video') ?? null;

  if (!wrapper || !video) {
    wrapper = document.createElement('div');
    wrapper.className = 'video-overlay__wrapper';

    video = document.createElement('video');
    video.autoplay = true;
    video.controls = true;
    video.append('Your browser does not support the video tag.');

    wrapper.appendChild(video);
    document.body.appendChild(wrapper);
  }

  return { wrapper, video };
}

function closeOverlay(): void {
  const wrapper = document.querySelector<HTMLElement>('.video-overlay__wrapper.visible');
  if (!wrapper) return;

  wrapper.classList.remove('visible');
  wrapper.querySelector('video')?.pause();
  document.querySelector('.generic-overlay-bg')?.classList.remove('visible');
  document.documentElement.style.overflow = '';
}

function watchVideo(event: MouseEvent, actionEl: HTMLElement): void {
  event.preventDefault();

  const rawConfig = actionEl.dataset.videoConfig;
  if (!rawConfig) return;

  let config: VideoConfig;

  try {
    config = JSON.parse(decodeURIComponent(rawConfig)) as VideoConfig;
  } catch (error) {
    console.error('medispa-watch-video: failed to parse data-video-config', error);
    return;
  }

  const sources = (config.sources ?? []).filter(
    (source): source is VideoSource & { url: string; mime_type: string } =>
      Boolean(source.url && source.mime_type),
  );

  if (!sources.length) {
    console.error('medispa-watch-video: video config has no playable sources');
    return;
  }

  const { wrapper, video } = getOrCreatePlayer();
  const bg = getOrCreateOverlayBg();

  video.querySelectorAll('source').forEach((sourceEl) => sourceEl.remove());

  for (const source of sources) {
    const sourceEl = document.createElement('source');
    sourceEl.src = source.url;
    sourceEl.type = source.mime_type;
    video.appendChild(sourceEl);
  }

  const [first] = sources;
  if (first.width) video.width = first.width;
  if (first.height) video.height = first.height;

  if (config.aspect_ratio) {
    wrapper.style.setProperty('--aspect-ratio', String(config.aspect_ratio));
  }

  wrapper.classList.add('visible');
  bg.classList.add('visible');
  document.documentElement.style.overflow = 'clip';

  // Swapping <source> children does not reload an existing <video>; load()
  // restarts it with the new sources.
  video.load();
  video.play().catch(() => {
    // Autoplay may be blocked; the user still has native controls.
  });
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  if (event.target.closest('.generic-overlay-bg.visible')) {
    closeOverlay();
    return;
  }

  const actionEl = event.target.closest<HTMLElement>('.js-action[data-action="watch-video"]');
  if (!actionEl) return;

  watchVideo(event, actionEl);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeOverlay();
});
